// Sample values: what Studio shows before the Exchange records it. They come
// from studio/content, never from code, and the UI marks each one Sample.
// See Design > UI, "Principles". Content also says which drawing each hub's card
// shows on All hubs, which is presentation, not a value, so it has no Sample mark.

import type { CardTone, CoverArt } from "../../../ui/src/index.ts";

/** A knowledge source and its health, until the Exchange records sources. `readBy` names the questions (intent ids) whose recipes read it. */
export type SourceSample = {
  name: string; type: "Graph" | "Table" | "API" | "Documents"; state: "Current" | "Healthy" | "Degraded" | "Stale" | "Not recorded";
  detail: string; cost?: string; readBy?: string[];
};
/** The drawing behind a hub card's disc on All hubs, and the card's tone. Studio names no hub, so content says which. */
export type HubCover = { art: CoverArt; tone: CardTone };
export type HubSample = { creditsBack?: Record<string, number>; sources?: SourceSample[]; cover?: HubCover };
/** Who Studio shows as signed in until sign-in is connected. */
export type SampleAccount = { name: string; email: string; org: string };
export type Share = { label: string; share: number };
export type Sample = {
  sample: true; account: SampleAccount; deliveryFeePerMillion: number; inputsPerMillion: number; hubs: Record<string, HubSample>;
  /** The share of earnings still inside the settlement window. */
  pendingShare?: number;
  /** Where answers were paid: by demand channel and by payment rail, as shares of answers. */
  channels?: Share[]; rails?: Share[];
};

/** A hub's knowledge sources: the sample ones where content has them, else the graphs the hub reads, with no health recorded. */
export function hubSources(sample: Sample, short: string, reads: ReadonlyArray<string>): (SourceSample & { sample: boolean })[] {
  const listed = sample.hubs[short]?.sources;
  if (listed?.length) return listed.map((s) => ({ ...s, sample: true }));
  return reads.map((r) => ({ name: r, type: "Graph" as const, state: "Not recorded" as const, detail: "The Exchange doesn't record freshness yet", sample: false }));
}

/** A price split into the delivery fee, inputs and the curator's margin, all per million answers. */
export function priceParts(sample: Sample, askPerMillion: number) {
  const fee = Math.min(sample.deliveryFeePerMillion, askPerMillion);
  const inputs = Math.min(sample.inputsPerMillion, askPerMillion - fee);
  return { fee, inputs, margin: askPerMillion - fee - inputs };
}

/** Earnings after the sample fee and inputs, as a share of earnings; null with no earnings. */
export function netMargin(sample: Sample, revenueUsd: number, answers: number): number | null {
  if (revenueUsd <= 0) return null;
  const costs = (answers * (sample.deliveryFeePerMillion + sample.inputsPerMillion)) / 1e6;
  return Math.max(0, revenueUsd - costs) / revenueUsd;
}
